import { useIsMobile } from '@lib/hooks'
import { IconCompactTree, IconDefaultTree, IconVerticalTree } from '@lib/icons'
import type { TreeType } from '@octopus-do/octopus-project-core'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  isCompactLayoutEnabled,
  selectCompactLayout,
} from '@store/reducers/artboard'
import cx from 'classnames'
import type React from 'react'
import { useSelector } from 'react-redux'

import { TREE_DEFAULT, TREE_MATRIX } from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { centerPage } from '@/helpers'
import { useScaleEvents } from '../../../../../../../Canvas/hooks/events'
import { Selector } from '../../../Selector'
import css from '../../SettingsItem.module.scss'

type Props = {
  inline?: boolean
  disabled?: boolean
}

const Frames: React.FC<Props> = ({ inline = false, disabled = false }) => {
  const isMobile = useIsMobile()
  const compact = useSelector(selectCompactLayout)

  const { onResetZoom } = useScaleEvents()

  const { changeProjectTreeType } = useProjectLegacyActions()

  const treeMode = useSelector(migrationProjectSelector.getProjectTreeType)
  const matrix = treeMode === TREE_MATRIX

  if (isMobile) {
    return null
  }

  const onClickMode = (mode: TreeType) => {
    setTimeout(() => {
      onResetZoom()
      centerPage()
    })

    sendGtmEvent(
      GtmTriggerName.PROJECT_LAYOUT,
      'CLick to change layout from sidebar'
    )

    changeProjectTreeType({ tree: mode })
  }

  return (
    <div
      className={cx(css.container, {
        [css.inline]: inline,
      })}
    >
      <div className={css.titleWrapper}>Layout</div>

      <div className={css.selectors}>
        <Selector
          active={!matrix && !compact}
          disabled={disabled}
          title="Default"
          classnames={{
            container: css.selector,
            icon: css.selectorIcon,
          }}
          icon={<IconDefaultTree />}
          onClick={() => onClickMode(TREE_DEFAULT)}
        />

        <Selector
          active={matrix}
          disabled={disabled}
          title="Vertical"
          classnames={{
            container: css.selector,
            icon: css.selectorIcon,
          }}
          icon={<IconVerticalTree />}
          onClick={() => onClickMode(TREE_MATRIX)}
        />
        {isCompactLayoutEnabled() && (
          <Selector
            active={compact}
            disabled={disabled}
            title="Compact"
            classnames={{ container: css.selector, icon: css.selectorIcon }}
            icon={<IconCompactTree />}
            onClick={() => {
              changeProjectTreeType({ tree: 'compact' })
            }}
          />
        )}
      </div>
    </div>
  )
}

export default Frames
